import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit, formatTimeRemaining } from "@/hooks/use-rate-limit";
import { Loader2, Radar, Clock } from "lucide-react";
import { Helmet } from "@/components/Helmet";
import type { Finding, ReportSummary, ScanResult } from "@/pages/portal/autoscan/types/customer";
import { LiveScanStatusPanel } from "@/components/live-scan/LiveScanStatusPanel";
import { WpScanResults } from "@/components/live-scan/WpScanResults";
import type { WpScanResult } from "@/types/scan";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import {
  buildLiveScanPdfUrl,
  buildLiveScanUrl,
  buildScanAuthHeaders,
  extractLiveLogText,
  extractFindings,
  extractScanResults,
  extractSummary,
  getPublicScanApiBase,
  isValidDomain,
  isValidEmail,
  normalizeDomain,
  parseScanStatus,
} from "@/lib/live-scan";

const PUBLIC_SCAN_API_BASE = getPublicScanApiBase();

const LIVE_SCAN_MAX_ATTEMPTS = Number(import.meta.env.VITE_LIVE_SCAN_MAX_PER_HOUR || 5);
const LIVE_SCAN_RATE_LIMIT = {
  maxAttempts: Number.isFinite(LIVE_SCAN_MAX_ATTEMPTS) ? LIVE_SCAN_MAX_ATTEMPTS : 999,
  windowMs: 60 * 60 * 1000,
};


const Scan = () => {
  const { toast } = useToast();
  const [liveScanEmail, setLiveScanEmail] = useState("");
  const [liveScanDomain, setLiveScanDomain] = useState("");
  const [isLiveScanSubmitting, setIsLiveScanSubmitting] = useState(false);
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<"idle" | "starting" | "running" | "completed" | "error">("idle");
  const [scanSummary, setScanSummary] = useState<ReportSummary | null>(null);
  const [, setScanFindings] = useState<Finding[]>([]);
  const [, setScanResults] = useState<ScanResult[]>([]);
  const [scanLog, setScanLog] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanToken, setScanToken] = useState<string | null>(null);
  const [scanTargetUrl, setScanTargetUrl] = useState<string | null>(null);
  const [pdfSending, setPdfSending] = useState(false);
  const [pdfSent, setPdfSent] = useState(false);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  const [liveScanAuthorized, setLiveScanAuthorized] = useState(false);
  const [wpScanResult, setWpScanResult] = useState<WpScanResult | null>(null);
  
  const logModalRef = useRef<HTMLPreElement | null>(null);
  const completionHandledRef = useRef<string | null>(null);

  const liveScanRateLimit = useRateLimit({ key: "live_scan", ...LIVE_SCAN_RATE_LIMIT });
  const normalizedDomain = useMemo(() => normalizeDomain(liveScanDomain), [liveScanDomain]);

  const finalizeScan = useCallback(async (payload: unknown) => {
    if (!scanJobId || completionHandledRef.current === scanJobId) return;
    completionHandledRef.current = scanJobId;
    const findings = extractFindings(payload);
    const summary = extractSummary(payload, findings);
    const results = extractScanResults(payload);
    setScanFindings(findings);
    setScanSummary(summary);
    setScanResults(results);
    setScanStatus("completed");
    setScanError(null);
    setPdfAvailable(true);

    if (!liveScanEmail || !normalizedDomain) return;
    try {
      setPdfSending(true);
      const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}/email`, {
        method: "POST",
        headers: buildScanAuthHeaders(scanToken, { "Content-Type": "application/json" }),
        body: JSON.stringify({ email: liveScanEmail }),
      });
      if (!res.ok) throw new Error("Failed to send report email");
      setPdfSent(true);
      toast({ title: "Report emailed", description: "We sent your PDF report to your inbox and our team." });
    } catch {
      toast({ title: "Report email failed", description: "The report is ready to download, but email delivery failed.", variant: "destructive" });
    } finally {
      setPdfSending(false);
    }
  }, [liveScanEmail, normalizedDomain, scanJobId, scanToken, toast]);

  const fetchScanStatus = useCallback(async () => {
    if (!scanJobId) return;
    const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}`, {
      headers: buildScanAuthHeaders(scanToken),
    });
    if (!res.ok) throw new Error("Failed to fetch scan status");
    const payload = await res.json();
    const job = (payload as { job?: { targetUrl?: string } }).job;
    if (job?.targetUrl) setScanTargetUrl(job.targetUrl);
    const liveLogText = extractLiveLogText(payload);
    if (liveLogText) setScanLog(liveLogText);
    const status = parseScanStatus(payload);
    setScanStatus(status);
    if (status === "completed") await finalizeScan(payload);
    if (status === "error") setScanError("Scan failed. Please try again or contact support.");
  }, [finalizeScan, scanJobId, scanToken]);

  useEffect(() => {
    if (!scanJobId || scanStatus === "completed" || scanStatus === "error") return;
    const interval = setInterval(() => { fetchScanStatus().catch(() => {}); }, 3000);
    return () => clearInterval(interval);
  }, [fetchScanStatus, scanJobId, scanStatus]);

  const handleLiveScanSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!liveScanEmail || !normalizedDomain) {
      toast({ title: "Missing info", description: "Enter an email and website domain.", variant: "destructive" });
      return;
    }
    if (!isValidDomain(normalizedDomain)) {
      toast({ title: "Invalid domain", description: "Please enter a valid domain name (e.g., example.com).", variant: "destructive" });
      return;
    }
    if (!isValidEmail(liveScanEmail)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    if (!liveScanRateLimit.checkLimit()) {
      toast({ title: "Too many scan requests", description: `You've reached the limit of ${LIVE_SCAN_RATE_LIMIT.maxAttempts} scans per hour. Please wait ${formatTimeRemaining(liveScanRateLimit.timeUntilReset)} before scanning again.`, variant: "destructive" });
      return;
    }
    if (!liveScanAuthorized) {
      toast({ title: "Authorisation required", description: "Confirm you own or are authorised to scan this site to continue.", variant: "destructive" });
      return;
    }

    setIsLiveScanSubmitting(true);
    setScanStatus("running");
    setScanError(null);
    setWpScanResult(null);
    setScanSummary(null);
    setScanLog("Connecting to target...\nDetecting site type...\nScanning for vulnerabilities...");

    try {
      const { data, error } = await supabase.functions.invoke("wp-scan", {
        body: { url: normalizedDomain, email: liveScanEmail },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      liveScanRateLimit.recordAttempt();

      setWpScanResult(data as WpScanResult);
      const vulnCount = data.vulnerabilities?.length || 0;
      setScanSummary({
        totalFindings: vulnCount,
        critical: data.vulnerabilities?.filter((v: any) => v.severity === "critical").length || 0,
        high: data.vulnerabilities?.filter((v: any) => v.severity === "high").length || 0,
        medium: data.vulnerabilities?.filter((v: any) => v.severity === "medium").length || 0,
        low: data.vulnerabilities?.filter((v: any) => v.severity === "low").length || 0,
        info: data.vulnerabilities?.filter((v: any) => v.severity === "info").length || 0,
      });
      setScanStatus("completed");
      setScanLog(`Scan complete: ${data.siteType?.toUpperCase() || "GENERIC"} site\n${vulnCount} vulnerabilities found\n${data.plugins?.length || 0} plugins detected\n${data.themes?.length || 0} themes detected\n${data.exposedFiles?.length || 0} exposed files found`);
      setIsResultsDialogOpen(true);

      toast({ title: "Scan Complete", description: `Found ${vulnCount} vulnerability${vulnCount !== 1 ? "ies" : "y"} on ${normalizedDomain}` });
    } catch (error) {
      console.warn("[WpScan] failed", { error: String(error) });
      const message = error instanceof Error ? error.message : "Scan failed. Please try again.";
      setScanStatus("error");
      setScanError(message);
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setIsLiveScanSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!scanJobId) return;
    const domain = normalizeDomain(scanTargetUrl || normalizedDomain || scanJobId);
    window.open(buildLiveScanPdfUrl(scanJobId, domain, scanToken), "_blank");
  };

  const statusPageHref = scanJobId ? buildLiveScanUrl(scanJobId, scanToken) : undefined;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Free Security Scan | ShadowScan</title>
        <meta name="description" content="Run a free, non-intrusive security scan on your WordPress site. Get a clear picture of vulnerabilities, exposed files, and security gaps." />
        <meta name="referrer" content="no-referrer" />
        <meta httpEquiv="Cache-Control" content="no-store" />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// FREE_SCAN</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Free WordPress <span className="text-gradient">Security Scan</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Enter your domain below and we'll run a non-intrusive external scan to surface potential risks.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto cyber-card rounded-xl p-8 border-primary/30">
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-muted-foreground mb-6 text-center">
                <a
                  href="https://shadowscanguard.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 font-medium text-primary underline hover:no-underline"
                >
                  <span>Powered by</span>
                  <img
                    src="/shadowscan-guard-logo.png"
                    alt=""
                    aria-hidden="true"
                    className="h-5 w-5 object-contain"
                  />
                  <span>ShadowScan Guard</span>
                </a>
              </div>
              <form onSubmit={handleLiveScanSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="live-email" className="text-sm font-mono text-muted-foreground">EMAIL *</Label>
                  <Input id="live-email" name="live-email" type="email" placeholder="your@email.com" required className="mt-2 bg-secondary/50" value={liveScanEmail} onChange={(e) => setLiveScanEmail(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="live-domain" className="text-sm font-mono text-muted-foreground">WEBSITE DOMAIN *</Label>
                  <Input id="live-domain" name="live-domain" placeholder="example.com" required className="mt-2 bg-secondary/50" value={liveScanDomain} onChange={(e) => setLiveScanDomain(e.target.value)} />
                </div>
                <div className="rounded-md border border-border/50 p-3 bg-muted/30 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Checkbox id="live-scan-authorised" checked={liveScanAuthorized} onCheckedChange={(checked) => setLiveScanAuthorized(checked === true)} className="mt-0.5" />
                    <label htmlFor="live-scan-authorised" className="leading-5">
                      I confirm I own this site or have explicit authorisation to run this scan. I understand this is a non-intrusive assessment and agree to the ShadowScan{" "}
                      <Link className="text-primary underline hover:no-underline" to="/terms">Terms of Service</Link>{" "}and{" "}
                      <Link className="text-primary underline hover:no-underline" to="/privacy">Privacy Policy</Link>.
                    </label>
                  </div>
                </div>
                <Button type="submit" size="lg" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground" disabled={isLiveScanSubmitting || scanStatus === "running" || scanStatus === "starting" || liveScanRateLimit.isLimited || !liveScanAuthorized}>
                  {liveScanRateLimit.isLimited ? (
                    <><Clock className="mr-2 h-5 w-5" />Wait {formatTimeRemaining(liveScanRateLimit.timeUntilReset)}</>
                  ) : isLiveScanSubmitting || scanStatus === "starting" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <><Radar className="mr-2 h-5 w-5" />Start Free Scan</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  {liveScanRateLimit.isLimited
                    ? `Rate limited: ${liveScanRateLimit.remainingAttempts} of ${LIVE_SCAN_RATE_LIMIT.maxAttempts} scans remaining this hour.`
                    : `${liveScanRateLimit.remainingAttempts} scans remaining this hour`}
                </p>
              </form>

              {scanStatus === "running" && (
                <div className="flex items-center justify-center gap-3 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  Scanning {normalizedDomain}…
                </div>
              )}

              {scanStatus === "error" && scanError && (
                <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {scanError}
                </div>
              )}

              {wpScanResult && scanStatus === "completed" && (
                <Button variant="outline" className="w-full mt-4" onClick={() => setIsResultsDialogOpen(true)}>
                  View Scan Results
                </Button>
              )}
            </div>
          </div>
        </section>
      </main>

      <Dialog open={isResultsDialogOpen} onOpenChange={setIsResultsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto cyber-card">
          <DialogHeader>
            <DialogTitle className="font-mono">Scan Results - {normalizedDomain}</DialogTitle>
            <DialogDescription>Security assessment findings for your site.</DialogDescription>
          </DialogHeader>
          {wpScanResult ? (
            <WpScanResults result={wpScanResult} />
          ) : scanStatus !== "idle" ? (
            <LiveScanStatusPanel status={scanStatus} scanLog={scanLog} scanSummary={scanSummary} scanError={scanError} pdfAvailable={pdfAvailable} pdfSending={pdfSending} pdfSent={pdfSent} onDownloadPdf={handleDownloadPdf} logRef={logModalRef} statusPageHref={statusPageHref} showEmailStatus />
          ) : null}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default Scan;
